import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import './SupportPage.css';
import Header from '../components/Header';

function SupportPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Save to Firestore
      await addDoc(collection(firestore, 'support_messages'), {
        ...formData,
        createdAt: serverTimestamp(),
        status: 'new'
      });

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-container">
      <Header />
      <div className="support-card">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Home
        </button>

        <h1 className="support-title">Support</h1>
        <p className="support-subtitle">
          Need help? We're here for you! Fill out the form below or reach out directly.
        </p>

        <div className="contact-info">
          <div className="contact-item">
            <span className="contact-icon">📧</span>
            <div>
              <p className="contact-label">Email</p>
              <a href="mailto:whoismostlikely@gmail.com" className="contact-value">whoismostlikely@gmail.com</a>
            </div>
          </div>
        </div>

        <div className="divider" />

        <form onSubmit={handleSubmit} className="support-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
            >
              <option value="">Select a topic</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="subscription">Subscription Issue</option>
              <option value="account">Account Issue</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Describe your issue or question..."
              rows={5}
              required
            />
          </div>

          {submitStatus === 'success' && (
            <div className="alert alert-success">
              ✅ Your message has been sent! We'll get back to you soon.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="alert alert-error">
              ❌ Something went wrong. Please try again or email us directly.
            </div>
          )}

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>

        <div className="faq-section">
          <h2 className="faq-title">Frequently Asked Questions</h2>
          
          <div className="faq-item">
            <h3>How do I cancel my subscription?</h3>
            <p>You can manage your subscription through your device's app store settings (App Store on iOS or Play Store on Android).</p>
          </div>

          <div className="faq-item">
            <h3>Why can't I see my purchase?</h3>
            <p>Try restoring purchases from the app's profile screen. If the issue persists, make sure you're signed in with the same Apple ID or Google account used for the purchase.</p>
          </div>

          <div className="faq-item">
            <h3>How many players can join a party?</h3>
            <p>Up to 20 players can join a single party.</p>
          </div>

          <div className="faq-item">
            <h3>Can I suggest new questions?</h3>
            <p>Absolutely! Use the form above with "Feature Request" as the subject and share your ideas.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportPage;
